import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { mailsService } from "../../services/mails.service";
import { isInboundAttachment } from "../../utils/attachmentKinds";
import { EMAIL_IMAGE_MAX_HEIGHT, EMAIL_SCOPE_ATTRIBUTE, prepareEmailHtml } from "../../utils/emailHtml";
import { ImageViewer } from "./AttachmentList";
import styles from "./EmailHtmlBody.module.css";

const INBOUND_ATTACHMENT_ID_PATTERN = /\/inbound-attachments\/(\d+)\/download\//;
const EMAIL_CONTENT_SELECTOR = "[data-email-content]";
const MAX_INLINE_IMAGE_RENEWALS = 10;

const isEmailContentImage = (element) =>
  element?.tagName === "IMG" && Boolean(element.closest(EMAIL_CONTENT_SELECTOR));

const findEmbeddedAttachment = (imageSource, attachments) => {
  const match = INBOUND_ATTACHMENT_ID_PATTERN.exec(imageSource || "");
  if (!match) return null;

  const attachmentId = Number(match[1]);
  return (
    attachments.find(
      (attachment) =>
        isInboundAttachment(attachment) && attachment.id === attachmentId,
    ) || null
  );
};

/**
 * Cuerpo HTML de un correo recibido, sanitizado, con sus propios estilos
 * acotados al mensaje, las imágenes inline en su lugar y el historial citado
 * plegado detrás de un botón. Lo comparten el hilo y el modal de lectura, así
 * el mensaje se ve igual en los dos.
 *
 * Si después de sanitizar el HTML no queda nada visible (por ejemplo, solo
 * tenía scripts o estilos) muestra ``fallback``, normalmente el texto plano.
 *
 * Las imágenes inline usan URLs firmadas que vencen a los pocos minutos: si
 * una no carga, se pide una URL nueva. Cada imagen recuerda qué URLs ya
 * fallaron (no las reintenta) y tiene un tope de renovaciones, así un hilo
 * abierto mucho tiempo puede renovar varias veces sin entrar en un ciclo. Las que son adjuntos del
 * correo se pueden ampliar con un click en el mismo visor de los adjuntos.
 */
const EmailHtmlBody = ({ html, attachments = [], className = "", maxImageHeight = EMAIL_IMAGE_MAX_HEIGHT, fallback = null }) => {
  const [isQuoteVisible, setIsQuoteVisible] = useState(false);
  const [viewerAttachment, setViewerAttachment] = useState(null);
  const closeViewer = useCallback(() => setViewerAttachment(null), []);
  const containerRef = useRef(null);
  const attachmentsRef = useRef(attachments);
  const { mainHtml, quotedHtml, hasVisibleContent, scopeId } = useMemo(
    () => prepareEmailHtml(html, { maxImageHeight }),
    [html, maxImageHeight],
  );
  const scopeAttributes = { [EMAIL_SCOPE_ATTRIBUTE]: scopeId };

  useEffect(() => {
    attachmentsRef.current = attachments;
  }, [attachments]);

  useEffect(() => {
    setIsQuoteVisible(false);
    setViewerAttachment(null);
  }, [html]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const renewalStateByImage = new WeakMap();

    const handleImageError = async (event) => {
      const image = event.target;
      if (!isEmailContentImage(image)) return;

      const failedSource = image.getAttribute("src");
      const attachment = findEmbeddedAttachment(failedSource, attachmentsRef.current);
      if (!attachment) return;

      const renewalState = renewalStateByImage.get(image) || { failedSources: new Set(), renewals: 0 };
      renewalStateByImage.set(image, renewalState);
      if (renewalState.failedSources.has(failedSource) || renewalState.renewals >= MAX_INLINE_IMAGE_RENEWALS) {
        return;
      }

      renewalState.failedSources.add(failedSource);
      renewalState.renewals += 1;
      try {
        image.src = await mailsService.fetchInboundAttachmentLink(attachment.id);
      } catch {
        image.alt = image.alt || attachment.name || "Imagen no disponible";
      }
    };

    container.addEventListener("error", handleImageError, true);
    return () => container.removeEventListener("error", handleImageError, true);
  }, []);

  useEffect(() => {
    containerRef.current?.querySelectorAll(`${EMAIL_CONTENT_SELECTOR} img`).forEach((image) => {
      const attachment = findEmbeddedAttachment(image.getAttribute("src"), attachments);
      if (attachment && attachment.available !== false) {
        image.dataset.zoomable = "true";
        image.setAttribute("role", "button");
        image.setAttribute("tabindex", "0");
        image.setAttribute("aria-label", `Ampliar imagen ${attachment.name || ""}`.trim());
      }
    });
  });

  const openZoomableImage = (event) => {
    const image = event.target;
    if (!isEmailContentImage(image) || image.dataset.zoomable !== "true") return;
    if (event.type === "keydown" && event.key !== "Enter" && event.key !== " ") return;

    const attachment = findEmbeddedAttachment(image.getAttribute("src"), attachments);
    if (!attachment) return;

    event.preventDefault();
    setViewerAttachment(attachment);
  };

  if (!hasVisibleContent) return fallback;

  return (
    <div
      ref={containerRef}
      className={`${styles.emailHtml} ${className}`}
      style={{ "--email-image-max-height": `${maxImageHeight}px` }}
      onClick={openZoomableImage}
      onKeyDown={openZoomableImage}
    >
      <div data-email-content {...scopeAttributes} dangerouslySetInnerHTML={{ __html: mainHtml }} />
      {quotedHtml && (
        <>
          <button
            type="button"
            className={styles.quoteToggle}
            aria-expanded={isQuoteVisible}
            onClick={() => setIsQuoteVisible((visible) => !visible)}
          >
            {isQuoteVisible
              ? "Ocultar mensaje anterior"
              : "Mostrar mensaje anterior"}
          </button>
          {isQuoteVisible && (
            <div
              data-email-content
              {...scopeAttributes}
              className={styles.quotedHistory}
              dangerouslySetInnerHTML={{ __html: quotedHtml }}
            />
          )}
        </>
      )}
      {viewerAttachment && (
        <ImageViewer attachment={viewerAttachment} onClose={closeViewer} />
      )}
    </div>
  );
};

export default EmailHtmlBody;
